import { useState, useContext, useEffect, forwardRef } from "react";
import {
  Dialog,
  DialogContent,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Divider,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { EventContext } from "../../../contexts/EventContext";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const UserPicker = ({ label, pairingEventId, selectedUser, onSelect }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const runSearch = async () => {
    if (!query || query.trim().length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }
    setSearching(true);
    const result = await request(
      `pairingevent/search?pairingEventId=${pairingEventId}&q=${encodeURIComponent(query)}`
    );
    setSearching(false);
    if (result.success) {
      setResults(result.attendees);
    } else {
      toast.error(result.error || "Search failed");
    }
  };

  const handleSelect = (user) => {
    onSelect(user);
    setQuery("");
    setResults([]);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery("");
    setResults([]);
  };

  if (selectedUser) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <div>
          <Typography variant="body2">
            {selectedUser.profile?.firstName} {selectedUser.profile?.lastName}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {selectedUser.email}
          </Typography>
        </div>
        <IconButton size="small" onClick={handleClear} aria-label="clear selection">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  return (
    <div>
      <TextField
        fullWidth
        label={label}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") runSearch();
        }}
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {searching ? (
                <CircularProgress size={16} />
              ) : (
                <IconButton onClick={runSearch} size="small">
                  <SearchIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      {results.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 200, overflowY: "auto" }}>
          <List dense disablePadding>
            {results.map((user) => (
              <ListItemButton key={user._id} onClick={() => handleSelect(user)}>
                <ListItemText
                  primary={`${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim() || user.email}
                  secondary={user.email}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </div>
  );
};

const DoNotPairDialog = ({ open, close }) => {
  const { pairingEvent } = useContext(EventContext);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user1, setUser1] = useState(null);
  const [user2, setUser2] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadBlocks();
    }
  }, [open]);

  const orgId = pairingEvent?.event?.org;

  const loadBlocks = async () => {
    setLoading(true);
    const result = await request(`pairingblock/?orgId=${orgId}`);
    setLoading(false);
    if (result.success) {
      setBlocks(result.blocks);
    } else {
      toast.error(result.error || "Failed to load blocks");
    }
  };

  const handleAddBlock = async () => {
    if (!user1 || !user2) {
      toast.error("Please select both attendees");
      return;
    }
    if (user1._id === user2._id) {
      toast.error("Please select two different attendees");
      return;
    }
    setSaving(true);
    const result = await request("pairingblock/create", "post", {
      user1Id: user1._id,
      user2Id: user2._id,
      reason: reason || undefined,
      orgId,
    });
    setSaving(false);
    if (result.success) {
      toast.success("Block added");
      setUser1(null);
      setUser2(null);
      setReason("");
      loadBlocks();
    } else {
      toast.error(result.error || "Failed to add block");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    const result = await request("pairingblock/delete", "post", { blockId, orgId });
    if (result.success) {
      toast.success("Block removed");
      setBlocks((prev) => prev.filter((b) => b._id !== blockId));
    } else {
      toast.error(result.error || "Failed to remove block");
    }
  };

  const formatName = (user) => {
    if (!user) return "Unknown";
    const name = `${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim();
    return name || user.email;
  };

  return (
    <Dialog open={open} onClose={close} fullScreen TransitionComponent={Transition}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={close} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Do Not Pair
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add New Block
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={5}>
            <UserPicker
              label="Search for Person 1"
              pairingEventId={pairingEvent?._id}
              selectedUser={user1}
              onSelect={setUser1}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <UserPicker
              label="Search for Person 2"
              pairingEventId={pairingEvent?._id}
              selectedUser={user2}
              onSelect={setUser2}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleAddBlock}
              disabled={saving || !user1 || !user2}
              sx={{ height: 40 }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : "Add Block"}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Existing Blocks
        </Typography>
        {loading ? (
          <CircularProgress />
        ) : blocks.length === 0 ? (
          <Typography color="textSecondary">No blocks have been added yet.</Typography>
        ) : (
          <List disablePadding>
            {blocks.map((block, index) => (
              <ListItem
                key={block._id}
                divider={index < blocks.length - 1}
                secondaryAction={
                  <IconButton edge="end" onClick={() => handleDeleteBlock(block._id)} aria-label="remove block">
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${formatName(block.user1)} & ${formatName(block.user2)}`}
                  secondary={block.reason || null}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DoNotPairDialog;
